// utils/calculationUtils.js
import { DateTime } from 'luxon';

/**
 * Calculates the donation schedule based on selected options and special conditions.
 *
 * @param {Object} params
 * @param {string} params.startDate - The first donation date (user-selected Ramadan start date).
 * @param {string} params.timeZone - The user's time zone.
 * @param {Array} params.selectedProducts - Array of selected products (each with price, quantity, etc.).
 * @param {string} params.donationOption - 'equal' or 'custom'.
 * @param {Array} params.selectedConditions - Array of selected condition strings.
 *        Allowed values: "friday", "lastTenNights", "oddNights", "laylatulQadr".
 * @param {Object} params.multipliers - Multipliers for each condition.
 *        Example: { friday: 2, lastTenNights: 3, oddNights: 4, laylatulQadr: 5 }
 * @param {string} params.currency - The selected currency code.
 * @param {number} params.numberOfNights - Total donation nights (typically 30).
 * @returns {Object} An object containing the donation schedule and any rounding difference.
 */
export const calculateDonations = ({
  startDate,
  timeZone,
  selectedProducts,
  donationOption,
  selectedConditions,
  multipliers,
  currency,
  numberOfNights,
  isLast10Nights = false,
}) => {
  // 1. Calculate the total donation amount based on selected products.
  const exactTotalAmount = selectedProducts.reduce((sum, p) => {
    const price = p[currency.toLowerCase()];
    const quantity = p.quantity || 0;
    return typeof price === 'number' ? sum + price * quantity : sum;
  }, 0);
  const totalAmountRounded = Math.round(exactTotalAmount);

  // 2. Get the current time in the user's selected timezone
  const currentDateTime = DateTime.local().setZone(timeZone);
  const currentDate = currentDateTime.startOf('day');
  const currentHour = currentDateTime.hour;
  const currentMinute = currentDateTime.minute;

  // 3. Build an array of donation dates (for the specified number of nights starting from startDate).
  let startDateLuxon = DateTime.fromISO(startDate, { zone: timeZone }).startOf('day');
  let _numberOfNights = numberOfNights;
  // 3. If the user-selected startDate is today and the time is past 00:30 AM, move the start date to the next day
  if (startDateLuxon.equals(currentDate) && (currentHour > 0 || (currentHour === 0 && currentMinute > 30))) {
    startDateLuxon = startDateLuxon.plus({ days: 1 });
    --_numberOfNights;
  }
  const donationDates = [];
  if (isLast10Nights && _numberOfNights > 10) {
    startDateLuxon = startDateLuxon.plus({ days: _numberOfNights - 10 }); // Move start to the last 10 nights
    _numberOfNights = 10;
  }
  // console.log("numberOfNights=",numberOfNights,_numberOfNights)
  for (let i = 0; i < _numberOfNights; i++) {
    donationDates.push(startDateLuxon.plus({ days: i }));
  }
  const totalNights = donationDates.length;

  // 3. For the 'custom' option, assign each day a share value based on all applicable conditions.
  //    We will add the multiplier values for each condition that applies.
  //    If no condition applies, the default share will be 1.
  const dayShares = donationDates.map((date, index) => {
    let share = 0; // start at 0 and add each condition's multiplier if applicable

    // Condition: Friday
    // (If selected and the day is Friday, add the Friday multiplier)
    if (selectedConditions.includes('friday') && date.weekday === 5) {
      share += multipliers.friday;
    }

    // Condition: Last 10 Nights
    // (If selected and the day is within the last 10 nights)
    if (selectedConditions.includes('lastTenNights') && index >= totalNights - 10) {
      share += multipliers.lastTenNights;
    }

    // Condition: Odd Nights (within the last 10)
    // (If selected and the day is one of the last 10 nights and its overall position is odd)
    // index >= totalNights - 10 && console.log('odd nights=', totalNights, index, index >= totalNights - 10, date.day);
    // if (selectedConditions.includes('oddNights') && index >= totalNights - 10 && (index + 1) % 2 === 1) {
    //   share += multipliers.oddNights;
    // }
    // if (selectedConditions.includes('oddNights') && index >= totalNights - 10 && date.day % 2 === 1) {
    //   share += multipliers.oddNights;
    // }
    // if (selectedConditions.includes('oddNights') && index >= totalNights - 10) {
    //   const firstNight = donationDates[totalNights - 10]; // First date in last 10 nights
    //   const isFirstNightOdd = firstNight.day % 2 === 1;

    //   // Select only odd nights
    //   if ((isFirstNightOdd && date.day % 2 === 1) || (!isFirstNightOdd && date.day % 2 === 0)) {
    //     share += multipliers.oddNights;
    //   }
    // }

    // if (selectedConditions.includes('oddNights') && index >= totalNights - 10) {
    //   const relativeNightNumber = index - (totalNights - 10) + 1;
    //   const firstNight = totalNights > 9 ? donationDates[totalNights - 10] : donationDates[0]; // First date in last 10 nights
    //   // const isFirstNightOdd = firstNight.day % 2 === 1;
    //   const isFirstNightOdd = totalNights > 9 ? firstNight.day % 2 === 1 : totalNights % 2 === 1;
    //   console.log("first-nitht=",totalNights,firstNight.day,index,date.day,isFirstNightOdd,totalNights % 2 === 1,relativeNightNumber)

    //   // Select only odd nights
    //   if ((isFirstNightOdd && date.day % 2 === 1) || (!isFirstNightOdd && date.day % 2 === 0)) {
    //     share += multipliers.oddNights;
    //   }
    // }

    if (selectedConditions.includes('oddNights') && index >= totalNights - 10) {
      const relativeNightNumber = index - (totalNights - 10) + 1;
      console.log('first-nitht=', totalNights, index, relativeNightNumber);
      if (relativeNightNumber % 2 === 1) {
        share += multipliers.oddNights;
      }
    }

    // Condition: Laylatul Qadr
    // (If selected and the day is the 27th night (index 26))
    // if (selectedConditions.includes('laylatulQadr') && index === 26) {
    if (selectedConditions.includes('laylatulQadr') && index === donationDates.length - 4) {
      share += multipliers.laylatulQadr;
    }

    // If no special condition applies, assign the default share of 1.
    if (share === 0) {
      share = 1;
    }

    return share;
  });

  // 4. Compute total shares and determine the per-share value.
  const totalShares = dayShares.reduce((sum, s) => sum + s, 0);
  const shareValue = totalShares > 0 ? totalAmountRounded / totalShares : 0;
  // console.log("donationDates=",donationDates,dayShares,totalShares,shareValue)
  // 5. Build the donation schedule: each day gets an amount = (its share) * (shareValue).
  const donationSchedule = donationDates.map((date, index) => ({
    date: date.toISODate(),
    shares: dayShares[index],
    amount: Math.round(dayShares[index] * shareValue),
    isMultiplierNight: dayShares[index] > 1,
    products: [],
    hasProduct: false,
    remainedAmount: 0,
  }));

  // 6. Adjust for any rounding differences so that the total equals totalAmountRounded.
  const sumRounded = donationSchedule.reduce((sum, day) => sum + day.amount, 0);
  let difference = totalAmountRounded - sumRounded;
  const adjustedDonationSchedule = donationSchedule.map((day, index) => {
    if (difference > 0 && index < difference) {
      return { ...day, amount: day.amount + 1 };
    } else if (difference < 0 && index < Math.abs(difference)) {
      return { ...day, amount: day.amount - 1 };
    }
    return day;
  });

  const finalSum = adjustedDonationSchedule.reduce((sum, day) => sum + day.amount, 0);
  const finalDifference = finalSum - totalAmountRounded;

  const handleAddPrd = () => {
    // Create a deep copy of the adjustedDonationSchedule (which is the schedule after rounding adjustments)
    const tempAdjustedSchedule = JSON.parse(JSON.stringify(adjustedDonationSchedule));

    // Calculate the total base amount from selectedProducts.
    let totalBaseForProducts = 0;
    selectedProducts.forEach(product => {
      // Multiply product.quantity by its price (e.g. product[currency.toLowerCase()])
      const productBase = product.quantity * product[currency.toLowerCase()];
      totalBaseForProducts += productBase;
    });

    // Build an array with each product and its percentage
    const productsWithPercentage = selectedProducts.map(product => {
      const productBase = product.quantity * product[currency.toLowerCase()];
      return {
        product,
        percentage: totalBaseForProducts ? (productBase * 100) / totalBaseForProducts : 0,
      };
    });

    const donationScheduleWithProducts = tempAdjustedSchedule.map(day => {
      // Step 1: For each product, calculate the raw allocation based on day.amount and product percentage.
      let initialAllocations = productsWithPercentage.map(item => {
        // Calculate raw allocation for the product
        const rawQuantity = (day.amount * item.percentage) / 100;
        return {
          product: JSON.parse(JSON.stringify(item.product)),
          rawQuantity, // For debugging if needed
          // We'll store the initial rounded value here:
          quantity: Math.round(rawQuantity),
        };
      });

      // Step 2: Sum the rounded quantities.
      let roundedTotal = initialAllocations.reduce((sum, p) => sum + p.quantity, 0);

      // Step 3: Compute the difference between day.amount and the sum of allocated quantities.
      const difference = day.amount - roundedTotal;

      // Step 4: Adjust quantities to fix the difference.
      // Loop over products (cycling if necessary) until the total allocated equals day.amount.
      let diffRemaining = difference;
      let idx = 0;
      while (diffRemaining !== 0) {
        // For a positive difference, increment; for a negative, decrement.
        if (diffRemaining > 0) {
          initialAllocations[idx].quantity += 1;
          diffRemaining--;
        } else if (diffRemaining < 0) {
          // Ensure we don't allocate a negative quantity.
          if (initialAllocations[idx].quantity > 0) {
            initialAllocations[idx].quantity -= 1;
            diffRemaining++;
          }
        }
        idx = (idx + 1) % initialAllocations.length;
      }

      // Step 5: Build the products array for this day.
      const productsAllocation = initialAllocations.map(item => {
        // Using product.creator as the key (adjust if you need a different identifier)
        // return { [item.product.creator]: { quantity: item.quantity } };
        item.product.quantity = item.quantity;
        // console.log("productsss=",item,)
        return item.product;
      });

      // Return the final day object including product allocations.
      return {
        date: day.date,
        shares: day.shares,
        amount: day.amount,
        isMultiplierNight: day.isMultiplierNight,
        products: productsAllocation,
        currency: currency,
        timeZone: timeZone,
        // Carry over any additional fields if needed:
        hasProduct: day.hasProduct,
        remainedAmount: day.remainedAmount,
      };
    });

    // Now, for example, store the final array in localStorage and log it.
    localStorage.setItem('DAILY_DONATION', JSON.stringify(donationScheduleWithProducts));
    // console.log('Final donation schedule with product allocations:', donationScheduleWithProducts);
  };
  handleAddPrd();
  return {
    donationSchedule: adjustedDonationSchedule,
    difference: finalDifference,
    totalNights: totalNights,
  };
};
